import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import BottomSheet from 'components/DesignSystem/BottomSheet';
import Section from 'components/Meteorology/Shared/ControlPanelSheet/Section';
import OptionRow from 'components/Meteorology/Shared/ControlPanelSheet/OptionRow';
import SheetActionButton from 'components/Layout/MobileShell/SheetActionButton';
import { store } from 'redux/store';
import {
  selectMFClassicComparatorBaseModel,
  selectMFClassicComparatorConfrontingModel,
  selectMFClassicComparatorVariable,
  setRequestTriggered,
} from 'redux/reducers/meteorologyFieldsClassicComparatorControlPanelReducer';
import useGetImagesParams from 'hooks/Meteorology/Fields/ClassicComparator/useGetImagesParams';
import useClassicComparatorBaseModel from 'utils/hooks/meteorology/classicComparator/useClassicComparatorBaseModel';
import useClassicComparatorConfrontingModel from 'utils/hooks/meteorology/classicComparator/useClassicComparatorConfrontingModel';
import useClassicComparatorUtility from 'utils/hooks/meteorology/classicComparator/useClassicComparatorUtility';
import useClassicComparatorVariable from 'utils/hooks/meteorology/classicComparator/useClassicComparatorVariable';
import useControlPanelOptions from 'utils/hooks/meteorology/useControlPanelOptions';
import ModelSection from './ModelSection';
import PeriodSection from './PeriodSection';

import './index.scss';

type PanelSection = 'variable' | 'confronting' | 'base' | 'period' | '';

export default function ControlPanelSheet({
  open,
  dismiss,
}: {
  open: boolean;
  dismiss: () => void;
}) {
  const [openSection, setOpenSection] = useState<PanelSection>('variable');
  const toggleSection = (section: PanelSection) => () => (
    setOpenSection((current) => (current === section ? '' : section))
  );

  const { variables, getModelOptions } = useControlPanelOptions('comparador');
  const { variable, setVariable } = useClassicComparatorVariable();
  const { resetPreferences } = useClassicComparatorUtility();
  const params = useGetImagesParams();

  const base = useClassicComparatorBaseModel();
  const confronting = useClassicComparatorConfrontingModel();

  const selectedVariable = useSelector(selectMFClassicComparatorVariable);
  const baseModel = useSelector(selectMFClassicComparatorBaseModel);
  const confrontingModel = useSelector(selectMFClassicComparatorConfrontingModel);

  const variableLabel = variables.find((v) => v.id === selectedVariable)?.label;
  // Modelos são exibidos em caixa alta no desktop (`text-uppercase` no ModelAccordion).
  const baseLabel = getModelOptions(baseModel).model.label.toUpperCase();
  const confrontingLabel = getModelOptions(confrontingModel).model.label.toUpperCase();

  const handleQuery = useCallback(() => {
    store.dispatch(setRequestTriggered(true));
    dismiss();
  }, [dismiss]);

  const handleClear = useCallback(() => {
    resetPreferences();
    setOpenSection('variable');
  }, [resetPreferences]);

  return (
    <BottomSheet
      open={open}
      dismiss={dismiss}
      title="Painel de controle"
      className="cc-mobile-panel"
    >
      <div className="cc-mobile-sheet-body">
        <Section
          label="Variável"
          valueText={variableLabel}
          open={openSection === 'variable'}
          onToggle={toggleSection('variable')}
        >
          {variables.map((option) => (
            <OptionRow
              key={option.id}
              label={option.label}
              selected={variable === option.id}
              onSelect={() => {
                setVariable(option.id);
                setOpenSection('');
              }}
            />
          ))}
        </Section>

        <Section
          label="Modelo confrontante"
          valueText={confrontingLabel}
          open={openSection === 'confronting'}
          onToggle={toggleSection('confronting')}
        >
          <ModelSection
            model={confronting.confrontingModel}
            setModel={confronting.setConfrontingModel}
            dateDiff={confronting.confrontingForecastDateDiff}
            setDateDiff={confronting.setConfrontingForecastDateDiff}
            runtime={confronting.confrontingRuntime}
            setRuntime={confronting.setConfrontingRuntime}
            member={confronting.confrontingMember}
            setMember={confronting.setConfrontingMember}
            rmv={confronting.confrontingRMV}
            setRMV={confronting.setConfrontingRMV}
            isBaseModel={false}
          />
        </Section>

        <Section
          label="Modelo base"
          valueText={baseLabel}
          open={openSection === 'base'}
          onToggle={toggleSection('base')}
        >
          <ModelSection
            model={base.baseModel}
            setModel={base.setBaseModel}
            dateDiff={base.baseForecastDateDiff}
            setDateDiff={base.setBaseForecastDateDiff}
            runtime={base.baseRuntime}
            setRuntime={base.setBaseRuntime}
            member={base.baseMember}
            setMember={base.setBaseMember}
            rmv={base.baseRMV}
            setRMV={base.setBaseRMV}
            isBaseModel
          />
        </Section>

        <Section
          label="Período de análise"
          valueText={undefined}
          open={openSection === 'period'}
          onToggle={toggleSection('period')}
        >
          <PeriodSection />
        </Section>
      </div>

      <div className="cc-mobile-sheet-footer">
        <SheetActionButton
          variant="secondary"
          className="cc-mobile-sheet-action"
          onClick={handleClear}
        >
          Limpar filtros
        </SheetActionButton>
        <SheetActionButton
          variant="confirm"
          className="cc-mobile-sheet-action"
          disabled={!params}
          onClick={handleQuery}
        >
          Realizar consulta
        </SheetActionButton>
      </div>
    </BottomSheet>
  );
}
